import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './styles/App.css';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import Header from './components/common/Header/Header';
import Hero from './components/sections/Hero/Hero';
import TreatmentInfo from './components/sections/TreatmentInfo/TreatmentInfo';
import About from './components/sections/About/About';
import BeforeAfter from './components/sections/BeforeAfter/BeforeAfter';
import Testimonials from './components/sections/Testimonials/Testimonials';
import Videos from './components/sections/Videos/Videos';
import FAQ from './components/sections/FAQ/FAQ';
import Contact from './components/sections/Contact/Contact';
import Footer from './components/common/Footer/Footer';
import WhatsAppButton from './components/common/WhatsAppButton/WhatsAppButton';
import SidePanel from './components/common/SidePanel/SidePanel';
import ThankYou from './components/pages/ThankYou';
import ConsoleSuppressor from './components/common/ConsoleSuppressor';

// reCAPTCHA v2 types
declare global {
  interface Window {
    grecaptcha: any;
  }
}

// reCAPTCHA Script Loader Component
const RecaptchaLoader: React.FC = () => {
  useEffect(() => {
    // Get reCAPTCHA site key from environment variables
    const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    
    if (!recaptchaSiteKey) {
      console.warn('reCAPTCHA site key not found in environment variables');
      return;
    }

    // Check if script already exists
    const existingScript = document.querySelector('script[src*="recaptcha"]');
    if (existingScript) {
      console.log('reCAPTCHA script already loaded');
      return;
    }

    console.log('Loading reCAPTCHA v2 script...');
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js';
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      console.log('reCAPTCHA v2 script loaded successfully');
      // Wait a bit for grecaptcha to be available
      setTimeout(() => {
        console.log('grecaptcha available:', !!window.grecaptcha);
        if (window.grecaptcha) {
          console.log('reCAPTCHA v2 ready to use!');
        }
      }, 1000);
    };

    script.onerror = () => {
      console.error('Failed to load reCAPTCHA script');
    };

    document.head.appendChild(script);

    return () => {
      // Don't remove script on unmount to avoid reloading
    };
  }, []);

  return null;
};

// Dynamic Meta Tags Component
const DynamicMetaTags: React.FC = () => {
  const { t } = useLanguage();

  useEffect(() => {
    // Update document title
    document.title = t('meta.title');
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', t('meta.description'));
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', t('meta.keywords'));
    }
    
    // Update Open Graph title
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', t('meta.ogTitle'));
    }
    
    // Update Open Graph description
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', t('meta.ogDescription'));
    }
    
    // Update Twitter title
    const twitterTitle = document.querySelector('meta[property="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', t('meta.ogTitle'));
    }
    
    // Update Twitter description
    const twitterDescription = document.querySelector('meta[property="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', t('meta.ogDescription'));
    }
  }, [t]);

  return null;
};

function App() {
  // Home page component
  const HomePage = () => (
    <>
      <Hero />
      <TreatmentInfo />
      <About />
      <BeforeAfter />
      <Testimonials />
      <Videos />
      <FAQ />
      <Contact />
    </>
  );

  return (
    <LanguageProvider>
      <Router>
        <div className="App">
          <ConsoleSuppressor />
          <RecaptchaLoader />
          <DynamicMetaTags />
          <Header />
          <main>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/thank-you" element={<ThankYou />} />
              <Route path="/tesekkur-ederiz" element={<ThankYou />} />
            </Routes>
          </main>
          <Footer />
          <WhatsAppButton />
          <SidePanel />
        </div>
      </Router>
    </LanguageProvider>
  );
}

export default App;